import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface SelectButtonOption<T> {
  label: ReactNode;
  value: T;
}

interface SelectButtonsProps<T> {
  options: SelectButtonOption<T>[];
  value?: T;
  onChange: (value: T) => void;
  label?: string;
  classes?: {
    container?: string;
    button?: string;
    buttonsContainer?: string;
  };
  disabled?: boolean;
  capitalize?: boolean;
}

export function SelectButtonsNew<T>({
  options,
  value,
  onChange,
  classes,
  label,
  disabled = false,
  capitalize = true,
}: SelectButtonsProps<T>) {
  return (
    <div className={cn({ 'w-full': !classes?.container?.includes('w-') }, classes?.container)}>
      {label && (
        <label className="mb-2 flex flex-row justify-between text-[10px] font-[Inter] font-medium uppercase leading-[18px] text-gray-900 text-opacity-[.64]">
          {label}
        </label>
      )}

      <div
        className={cn(
          'flex min-h-[36px] items-center overflow-hidden rounded-md border border-gray-200 bg-gray-100 divide-x divide-gray-200',
          classes?.buttonsContainer,
        )}
      >
        {options.map((option, index) => (
          <div key={index} className="flex w-full items-center justify-center">
            <button
              type="button"
              disabled={disabled}
              onClick={e => {
                onChange(option.value);
                e.currentTarget.blur();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              className={cn(
                'mx-[2px] min-h-[32px] w-full select-none rounded-sm px-2 py-1 text-sm leading-[20px] transition-all duration-300',
                {
                  'border border-blue-700 bg-white': option.value === value,
                  'text-gray-600 hover:text-gray-900': option.value !== value,
                  capitalize,
                },
                classes?.button,
              )}
            >
              {option.label}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
